import React from 'react';
import { ShieldCheck, ExternalLink } from 'lucide-react';
import Seo, { breadcrumbSchema } from '@/components/Seo';
import Breadcrumbs from '@/components/Breadcrumbs';
import Reveal from '@/components/Reveal';
import CTAPanel from '@/components/CTAPanel';
import { RERA_LIST } from '@/data/siteData';

const Rera = () => (
  <>
    <Seo
      title="Provident Palm Vista RERA | MahaRERA Registration Details"
      description="Provident Palm Vista RERA registrations — wing-wise MahaRERA numbers for B1, B2, C1, C2, D1, D2, D3 and D4. Verify on maharera.mahaonline.gov.in."
      path="/provident-palm-vista-rera"
      jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'RERA', path: '/provident-palm-vista-rera' }])]}
    />
    <section className="pt-28 md:pt-36 pb-16 md:pb-24 bg-ivory min-h-[70vh]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ name: 'RERA' }]} />
        <Reveal>
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-9 w-9 text-gold" strokeWidth={1.5} />
            <div>
              <p className="text-xs font-semibold tracking-[0.25em] uppercase text-gold">Compliance</p>
              <h1 className="font-display text-4xl sm:text-5xl font-medium text-palm leading-[1.08] tracking-tight">Provident Palm Vista RERA Details</h1>
            </div>
          </div>
          <p className="text-[15px] text-ink/75 mt-5 leading-relaxed">
            Provident Palm Vista is registered under the Maharashtra Real Estate Regulatory Authority (MahaRERA). Registrations are held wing-wise, as listed below. You can verify any registration number on the official MahaRERA portal.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10 overflow-hidden rounded-2xl border border-line bg-white shadow-sm" data-testid="rera-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-palm text-ivory text-left">
                <th className="px-6 py-4 font-semibold">Wing</th>
                <th className="px-6 py-4 font-semibold">MahaRERA Registration Number</th>
              </tr>
            </thead>
            <tbody>
              {RERA_LIST.map((r) => (
                <tr key={r.rera} className="border-t border-line hover:bg-paper/60 transition-colors">
                  <td className="px-6 py-4 font-semibold text-palm">{r.wing}</td>
                  <td className="px-6 py-4 font-mono text-ink/80">{r.rera}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>

        <Reveal delay={0.15} className="mt-6">
          <a href="https://maharera.mahaonline.gov.in" target="_blank" rel="noopener noreferrer" data-testid="rera-portal-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">
            Verify on maharera.mahaonline.gov.in <ExternalLink className="h-4 w-4" />
          </a>
        </Reveal>

        <Reveal delay={0.2} className="mt-10 bg-paper border border-line rounded-2xl p-6">
          <h2 className="font-semibold text-palm text-sm">Disclaimer</h2>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            Project specifications, plans, dimensions, images, amenities and other details are subject to applicable approvals and agreements. This website is an independent information resource and not the official developer website. Please refer to the applicable project documentation, MahaRERA records and the agreement for sale for definitive details.
          </p>
        </Reveal>
      </div>
    </section>
    <CTAPanel title="Ask Us for RERA Documents & Cost Sheet" />
  </>
);

export default Rera;
